import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getApiZones } from '@/api/gen/api';
import { useDebounce } from './useDebounce';

// minimal zone-like shape accepted by this selector
export interface ZoneOption {
  id: number;
  zoneName: string;
  // allow extra fields from API types
  [key: string]: any;
}

interface ZoneSelectorProps {
  plantId: number;
  value?: number | null; // zoneId
  onChange: (zoneId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

export default function ZoneSelector({
  plantId,
  value,
  onChange,
  placeholder = 'Selecciona una zona',
  disabled = false,
  required = false,
  label = 'Zona',
}: ZoneSelectorProps) {
  console.log('ZoneSelector plantId:', plantId);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Store previous plantId to detect changes
  const prevPlantIdRef = useRef<number | null>(null);

  // Reset input value and selected value when plantId changes to a different value
  useEffect(() => {
    if (prevPlantIdRef.current !== null && prevPlantIdRef.current !== plantId) {
      setInputValue('');
      setSearchQuery('');
      onChange(null);
    }
    prevPlantIdRef.current = plantId;
  }, [plantId, onChange]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['zones', 'infinite', plantId, debouncedSearch],
    queryFn: ({ pageParam }) =>
      getApiZones({
        isPaginated: true,
        page: pageParam as number,
        pageSize: 20,
        PlantId: plantId,
        SearchString: debouncedSearch || undefined,
      }).then((response: any) => response),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0);
      if (loaded < (lastPage.totalCount || 0)) {
        return (lastPage.page || 1) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const options = useMemo(() => {
    console.log('useMemo options running');
    const allOpts = (data?.pages?.flatMap(page => page?.items || []) ||
      []) as ZoneOption[];
    console.log('ZoneSelector data:', data);
    console.log('ZoneSelector allOpts:', allOpts);
    // Ensure uniqueness by id
    const uniqueOpts = [...new Map(allOpts.map(opt => [opt.id, opt])).values()];
    // Always include the selected zone if it exists and is not already in the list
    const selectedZone = allOpts.find(zone => zone.id === value);
    if (value && selectedZone && !uniqueOpts.some(opt => opt.id === value)) {
      uniqueOpts.unshift(selectedZone);
    }
    return uniqueOpts;
  }, [data, value]);

  // Find the selected zone based on zoneId
  const selectedZone = useMemo(() => {
    return options.find(zone => zone.id === value) || null;
  }, [options, value]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const renderOptionFn = (props: any, option: ZoneOption) => {
    const { key, ...other } = props;
    return (
      <li key={key} {...other}>
        {option.zoneName}
      </li>
    );
  };

  return (
    <Autocomplete
      disablePortal
      freeSolo
      value={selectedZone}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          onChange(newValue.id);
        } else {
          // If string or null, do nothing or deselect if needed
          onChange(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          setSearchQuery(newInputValue);
          if (value) {
            onChange(null);
          }
        }
      }}
      options={options}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.zoneName
      }
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error ? 'Error cargando zonas' : undefined}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={renderOptionFn}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      noOptionsText={isLoading ? 'Cargando...' : 'No hay opciones'}
    />
  );
}
